<template>
  <div class="transactions__filters bg-block mb-6 lg:mb-12 shadow-primaryBlock rounded">
    <div class="mr-14 mb-12 lg:mb-0">
      <div class="text-text-label text-xs mb-3-5">{{ $t('transactionTypes') }}</div>
      <div class="flex items-start space-x-10">
        <Checkbox :checked="filters.types.expense"
                  @change="handleType({name:'expense',val:$event})">
          {{ $t('expenses') }}
        </Checkbox>
        <Checkbox :checked="filters.types.income"
                  @change="handleType({name:'income',val:$event})">
          {{ $t('incomes') }}
        </Checkbox>
        <Checkbox :checked="filters.types.transfer"
                  @change="handleType({name:'transfer',val:$event})">
          {{ $t('transfers') }}
        </Checkbox>
      </div>
    </div>
    <div class="grid grid-cols-12 col-gap-8 lg:pt-2-7 flex-auto">
      <VueCtkDateTimePicker class="col-span-3"
                            v-model="date_range"
                            format="YYYY-MM-DD"
                            formatted="L"
                            :range="true"
                            :auto-close="true"
                            :custom-shortcuts="datepickerShortcuts"
                            :locale="$store.state.i18n.lang"
                            :label="$t('dateLabel')"
                            :no-label="false"
                            :no-clear-button="true"
                            :no-button="true"
                            color="#486680"/>
      <AccountsSelect class="col-span-3"
                      :value="filters.account"
                      :label="$t('accountsLabel')"
                      :accounts="accounts_list"
                      :account_groups="accounts_by_groups_list"
                      :grouped="accounts_by_groups.length > 1"
                      track-by="id"
                      :allow-empty="false"
                      @input="handleFilterAccount"/>
      <ExpenseSelect v-if="filters.types.expense || filters.types.transfer"
                     class="col-span-3"
                     :label="$t('expenseLabel')"
                     :selected="filters.expense_category"
                     :expenses="expenses_list"
                     :expenses_groups="expenses_by_groups_list"
                     :grouped="expenses_by_groups.length > 1"
                     track-by="id"
                     @input="handleFilterExpense"/>
      <BaseSelect v-if="filters.types.income" class="col-span-3"
                  v-model="filters.income_source"
                  :options="incomes_list"
                  track-by="id"
                  :select-label="$t('incomeLabel')"
                  label="title"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  @select="handleFilterIncome">
        <template v-slot:noOptions="">
          <span style="white-space: normal;">
            У вас пока нет сформированных источников дохода, вы можете добавить их
            <router-link :to="{name:'settings-income',params:{...$router.params}}">здесь</router-link>
          </span>
        </template>
      </BaseSelect>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import Checkbox from '@/components/UI/BaseCheckbox.vue';
import AccountsSelect from '@/components/UI/BaseSelect/AccountsSelect.vue';
import ExpenseSelect from '@/components/UI/BaseSelect/ExpenseSelect.vue';
import LoaderBlock from '@/components/UI/LoaderBlock.vue';
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";

export default {
  name: "TransactionFilters",
  components: {BaseSelect, Checkbox, AccountsSelect, ExpenseSelect, LoaderBlock},
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/transactions', ['filters']),
    ...mapState('space/accounts', ['accounts']),
    ...mapState('space/expenses', ['expenses']),
    ...mapState('space/incomes', ['incomes']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    ...mapGetters('space/expenses', ['expenses_by_groups']),
    ...mapGetters('space', ['spaceFirstTransactions']),
    accounts_list: {
      get() {
        let first = {id: null, title: this.$t('allAccounts')}
        return [first, ...this.accounts.data];
      }
    },
    expenses_list: {
      get() {
        let first = {id: null, title: this.$t('allCategories')}
        return [first, ...this.expenses.data];
      }
    },
    incomes_list: {
      get() {
        let first = {id: null, title: this.$t('allSources')}
        return [first, ...this.incomes];
      }
    },
    accounts_by_groups_list: {
      get() {
        let first = {
          id: null, title: '', childs: [{id: null, title: this.$t('allAccounts')}]
        }
        return [first, ...this.accounts_by_groups];
      }
    },
    expenses_by_groups_list: {
      get() {
        let first = {
          id: null,
          title: '',
          childs: [{id: null, title: this.$t('allCategories')}]
        }
        return [first, ...this.expenses_by_groups];
      }
    },
    date_range: {
      get() {
        return this.filters.date_range;
      },
      set(date) {
        this.applyDateRange(date)
        this.$emit('filtersChanged')
      }
    },
    datepickerShortcuts: {
      get() {
        return [{
          key: 'thisMonth',
          label: 'Месяц',
          value: () => ({start: this.$moment().startOf('month'), end: this.$moment()})
        },
          {
            key: 'thisQuarter',
            label: 'Квартал',
            value: () => ({start: this.$moment().startOf('quarter'), end: this.$moment()})
          },
          {
            key: 'thisYear',
            label: 'Этот год',
            value: () => ({start: this.$moment().startOf('year'), end: this.$moment()})
          },
          {
            key: 'year',
            label: 'Год',
            value: () => ({start: this.$moment().subtract(12, 'month'), end: this.$moment()})
          },
          {key: 'all', label: 'С начала', value: this.spaceFirstTransactions},
        ]
      }
    },
  },
  methods: {
    ...mapActions('space/transactions', ['applyDateRange']),
    ...mapMutations('space/transactions', [
      'setType', 'setFilterByName',
      'clearFilters']),
    handleType(data) {
      this.setType(data);
      if (data.val) {
        if (data.name === 'expense') {
          this.setFilterByName({name: 'income_source', value: this.incomes_list[0]})
        }
        if (data.name === 'income') {
          this.setFilterByName({name: 'expense_category', value: this.expenses_list[0]})
        }
      }
      this.$emit('filtersChanged')
    },
    handleFilterAccount(account) {
      this.setFilterByName({name: 'account', value: account})
      this.$emit('filtersChanged')
    },
    handleFilterExpense(expense_category) {
      this.setFilterByName({name: 'expense_category', value: expense_category})
      if (!!expense_category.id) {
        this.setType({name: 'expense', val: true});
        this.setType({name: 'income', val: false});
        this.setType({name: 'transfer', val: true});
      }
      this.$emit('filtersChanged')
    },
    handleFilterIncome(income_source) {
      this.setFilterByName({name: 'income_source', value: income_source})
      if (!!income_source.id) {
        this.setType({name: 'expense', val: false});
        this.setType({name: 'income', val: true});
        this.setType({name: 'transfer', val: false});
      }
      this.$emit('filtersChanged')
    },
    setInitialRange() {
      let now = this.$moment().format('YYYY-MM-DD'),
          month_start = this.$moment().startOf('month').format('YYYY-MM-DD');

      this.applyDateRange({
        start: month_start,
        end: now
      })
    }
  },
  created() {
    if (!this.filters.date_range.start) {
      this.setInitialRange()
      this.setFilterByName({name: 'account', value: this.accounts_list[0]})
      this.setFilterByName({name: 'expense_category', value: this.expenses_list[0]})
      this.setFilterByName({name: 'income_source', value: this.incomes_list[0]})
    }
  },
  i18n: {
    messages: {
      en: {
        transactionTypes: 'Transaction types',
        incomes: 'Incomes',
        expenses: 'Expenses',
        transfers: 'Transfers',
        dateLabel: 'Date period',
        accountsLabel: 'From account',
        allAccounts: 'All accounts',
        incomeLabel: 'Income source',
        allSources: 'All sources',
        expenseLabel: 'Expense category',
        allCategories: 'All categories'
      },
      ru: {
        transactionTypes: 'Типы операций',
        incomes: 'Доходы',
        expenses: 'Расходы',
        transfers: 'Переводы',
        dateLabel: 'Дата операции',
        accountsLabel: 'Со счета',
        allAccounts: 'Все счета',
        incomeLabel: 'Источник дохода',
        allSources: 'Все источники',
        expenseLabel: 'Категория расходов',
        allCategories: 'Все категории'
      }
    }
  }
}
</script>

<style lang="scss">
.transactions__filters {
  display: flex;
  padding: 2.9rem 3.5rem 2.4rem 4.1rem;
  @media (max-width: 1229px) {
    flex-wrap: wrap;
    padding: 2rem 1.8rem 2.4rem 2rem;
  }
}
</style>
